/**
 * ResumeCartChip — persistent TopBar affordance (v0.1.19).
 *
 * When SessionContext has an activeCartId AND the current route isn't
 * already that cart, show a pill that jumps back. Closes the "I got pulled
 * away mid-cart and now I need to remember the URL" gap that SPFx solved
 * with its sticky cart sidebar — in v3 the shell is top-only, so the chip
 * carries the same affordance.
 *
 * Dismiss (×) drops the pin; next cart touch re-arms it.
 */
import { useEffect, useState } from 'react';
import { X, FolderOpen } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router';
import { useSession } from '@/contexts/SessionContext';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useConfig } from '@/contexts/ConfigContext';
import { identifyCart, cartTitle, type CartIdentity } from '@/utils/cartLabel';
import './ResumeCartChip.css';

export function ResumeCartChip(): JSX.Element | null {
  const { activeCartId, setActiveCart } = useSession();
  const navigate = useNavigate();
  const loc = useLocation();
  const client = useApiClient();
  const { config } = useConfig();

  // NAME THE CART, DO NOT SPELL ITS GUID (Bill 2026-09-11). The chip read
  // "Resume cart · 9d3c2df4" — eight hex characters that mean nothing to an
  // FSR, identify nothing at a glance, and are impossible to tell apart when
  // two carts are open. Same cart, same shared labeller Home and New Acct use.
  const [identity, setIdentity] = useState<CartIdentity | null>(null);
  useEffect(() => {
    let live = true;
    setIdentity(null);
    if (!activeCartId) return undefined;
    void client.retail.carts
      .get(activeCartId)
      .then((res) => {
        if (!live) return;
        // CartStateResult is { success, cart }, NOT { data } - reading .data
        // is why the chip rendered a bare "Resume cart" in 0.115.0.
        const c = (res as { cart?: { status?: string; peopleJson?: string; productsJson?: string } })?.cart;
        // A committed or archived cart cannot be resumed - the pin was
        // outliving the cart (Bill 2026-09-11: chip named a July cart that
        // was long since committed while All Carts showed something else).
        const st = (c?.status ?? '').toLowerCase();
        if (st === 'committed' || st === 'archived') {
          setActiveCart(null);
          return;
        }
        setIdentity(identifyCart(c?.peopleJson, c?.productsJson, (code) => config?.products?.find((p) => p.code === code)?.name));
      })
      .catch(() => {
        // A failed lookup must not remove the chip — the resume affordance is
        // the point. Fall back to the generic wording below.
        if (live) setIdentity(null);
      });
    return () => {
      live = false;
    };
  }, [activeCartId, client, config]);

  if (!activeCartId) return null;

  const label = identity ? cartTitle(identity) ?? null : null;
  // The hover says WHO and WHAT - "Resume the cart you were working on" told
  // the user nothing they could act on (Bill 2026-09-11).
  const hover = identity?.who
    ? [
        'Resume this cart',
        `Customer: ${identity.who}${identity.others > 0 ? ` (+${identity.others} more)` : ''}`,
        identity.what ? `Product: ${identity.what}` : 'Product: none selected yet',
      ].join('\n')
    : 'Resume this cart';

  // Don't render when the user is already on the cart's detail page.
  if (loc.pathname === `/rao/${activeCartId}` || loc.pathname.startsWith(`/rao/${activeCartId}/`)) {
    return null;
  }


  return (
    <div className="resume-cart-chip" data-tour="topbar-resume" role="group" aria-label="Resume in-flight cart">
      <button
        type="button"
        className="resume-cart-chip__open"
        onClick={() => navigate(`/rao/${activeCartId}`)}
        title={hover}
        aria-label={label ? `Resume — ${label}` : 'Resume cart'}
      >
        <FolderOpen size={12} aria-hidden />
        {/* Label hides on a phone (<= 640px); the icon + aria-label remain. */}
        <span className="resume-cart-chip__label">{label ? `Resume — ${label}` : 'Resume cart'}</span>
      </button>
      <button
        type="button"
        className="resume-cart-chip__dismiss"
        onClick={() => setActiveCart(null)}
        aria-label="Stop tracking this cart"
        title="Stop tracking this cart"
      >
        <X size={12} aria-hidden />
      </button>
    </div>
  );
}
